import { useState, useMemo, useCallback } from 'react';
import { PACKAGING_SIZES, COVERAGE_RATES, SUBSTRATES, PRIMERS, PRICING } from '../config/products';

export const useCalculator = () => {
  const [selectedSubstrate, setSelectedSubstrate] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [customArea, setCustomArea] = useState(0.1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [textureType, setTextureType] = useState(null); // null until selected, then 'smooth' or 'textured'
  const [includeSealer, setIncludeSealer] = useState(null); // null until selected, then true or false

  // Get area based on selection
  const area = useMemo(() => {
    if (!selectedSize) return 0;
    if (selectedSize.id === 'custom') return customArea;
    return selectedSize.areaM2;
  }, [selectedSize, customArea]);

  // Get substrate details
  const substrateDetails = useMemo(() => {
    if (!selectedSubstrate) return null;
    return Object.values(SUBSTRATES).find((s) => s.id === selectedSubstrate);
  }, [selectedSubstrate]);

  // Get appropriate primer
  const selectedPrimer = useMemo(() => {
    if (!substrateDetails) return null;
    return substrateDetails.absorbent ? PRIMERS.solan : PRIMERS.quartz;
  }, [substrateDetails]);

  // Calculate layers
  const layers = useMemo(() => {
    return substrateDetails?.layers || 2;
  }, [substrateDetails]);

  // Check for warnings
  const hasWarnings = useMemo(() => {
    return substrateDetails?.riskLevel === 'high' || substrateDetails?.riskLevel === 'medium';
  }, [substrateDetails]);

  // Calculate material quantities (in grams for solids, ml for liquids)
  const calculations = useMemo(() => {
    if (area <= 0) return null;

    // Microlit calculation (grams) - 2kg/m² for textured, 1kg/m² for smooth
    const microlitRate = textureType === 'textured' 
      ? COVERAGE_RATES.microlit.thickCoat 
      : COVERAGE_RATES.microlit.perLayer;
    const microlitTotalGrams = area * microlitRate * layers;
    
    // Primer calculation (grams) - Solan: 333g/m², Quartz: 1000g/m²
    const primerRate = substrateDetails?.absorbent 
      ? COVERAGE_RATES.primerSolan.perLayer 
      : COVERAGE_RATES.primerQuartz.perLayer;
    const primerTotalGrams = area * primerRate;
    
    // Sealer calculation (ml) - 200ml/m²
    const sealerTotalMl = includeSealer ? area * COVERAGE_RATES.sealer.perLayer : 0;

    // Calculate packaging for each product
    const calculatePackaging = (totalAmount, packages) => {
      return packages.map((pkg) => {
        const packsNeeded = Math.ceil(totalAmount / pkg.size);
        const totalInPacks = packsNeeded * pkg.size;
        const leftover = totalInPacks - totalAmount;
        return {
          ...pkg,
          packsNeeded,
          totalInPacks,
          leftover: Math.round(leftover * 10) / 10,
          leftoverPercent: Math.round((leftover / totalInPacks) * 100),
        };
      });
    };

    const findOptimal = (options) => {
      return options.reduce((best, current) => {
        if (current.leftoverPercent < best.leftoverPercent) return current;
        return best;
      });
    };

    // Calculate pricing
    const microlitPricePerGram = PRICING.microlit / 1000; // ₪400/kg = ₪0.4/g
    const primerPricePerGram = substrateDetails?.absorbent 
      ? PRICING.primerSolan / 1000  // ₪200/L ≈ ₪200/kg = ₪0.2/g
      : PRICING.primerQuartz / 1000; // ₪150/kg = ₪0.15/g
    const sealerPricePerMl = PRICING.sealer / 1000; // ₪150/L = ₪0.15/ml

    const microlitPackaging = calculatePackaging(microlitTotalGrams, PACKAGING_SIZES.microlit);
    const primerPackaging = calculatePackaging(
      primerTotalGrams, 
      substrateDetails?.absorbent ? PACKAGING_SIZES.primerSolan : PACKAGING_SIZES.primerQuartz
    );
    const sealerPackaging = includeSealer 
      ? calculatePackaging(sealerTotalMl, PACKAGING_SIZES.sealer) 
      : null;

    const microlitOptimal = findOptimal(microlitPackaging);
    const primerOptimal = findOptimal(primerPackaging);
    const sealerOptimal = sealerPackaging ? findOptimal(sealerPackaging) : null;

    // Calculate estimated prices based on optimal packaging
    const microlitPrice = Math.round(microlitOptimal.totalInPacks * microlitPricePerGram);
    const primerPrice = Math.round(primerOptimal.totalInPacks * primerPricePerGram);
    const sealerPrice = sealerOptimal ? Math.round(sealerOptimal.totalInPacks * sealerPricePerMl) : 0;
    const totalPrice = microlitPrice + primerPrice + sealerPrice;

    return {
      microlit: {
        totalGrams: Math.round(microlitTotalGrams),
        totalKg: Math.round(microlitTotalGrams / 100) / 10, // Round to 1 decimal
        packaging: microlitPackaging,
        optimal: microlitOptimal,
        estimatedPrice: microlitPrice,
        unit: 'גרם',
        productName: textureType === 'textured' ? 'מיקרו מינרל סמיך' : 'מיקרו מינרל דליל',
        productNameEn: textureType === 'textured' ? 'MicroMineral Thick' : 'MicroMineral Diluted',
      },
      primer: {
        totalGrams: Math.round(primerTotalGrams),
        totalKg: Math.round(primerTotalGrams / 100) / 10,
        packaging: primerPackaging,
        optimal: primerOptimal,
        type: selectedPrimer,
        estimatedPrice: primerPrice,
        unit: 'גרם',
      },
      sealer: sealerPackaging ? {
        totalMl: Math.round(sealerTotalMl),
        packaging: sealerPackaging,
        optimal: sealerOptimal,
        estimatedPrice: sealerPrice,
        unit: 'מ"ל',
        productName: 'סילר אקרוסיל',
        productNameEn: 'Acrosil Sealer',
      } : null,
      area,
      layers,
      totalPrice,
    };
  }, [area, layers, textureType, includeSealer, substrateDetails, selectedPrimer]);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedSubstrate(null);
    setSelectedSize(null);
    setCustomArea(0.1);
    setSelectedColor(null);
    setTextureType(null);
    setIncludeSealer(null);
  }, []);

  // Generate summary text
  const generateSummary = useCallback((lang = 'he') => {
    const isHebrew = lang === 'he';
    
    let summary = isHebrew 
      ? `=== סיכום הזמנה - Microlit Mineral Coating ===\n\n`
      : `=== Order Summary - Microlit Mineral Coating ===\n\n`;

    if (substrateDetails) {
      summary += isHebrew ? `משטח: ${substrateDetails.nameHe}\n` : `Surface: ${substrateDetails.nameEn}\n`;
    }
    
    if (selectedSize) {
      summary += isHebrew 
        ? `גודל: ${selectedSize.nameHe}\n`
        : `Size: ${selectedSize.nameEn}\n`;
    }
    
    summary += isHebrew ? `שטח: ${area} מ"ר\n` : `Area: ${area} m²\n`;
    
    if (selectedColor) {
      summary += isHebrew
        ? `גוון: ${selectedColor.nameHe}\n`
        : `Color: ${selectedColor.nameEn}\n`;
    }
    
    summary += isHebrew
      ? `גימור: ${textureType === 'textured' ? 'טקסטורה (סמיך)' : 'חלק (דליל)'}\n`
      : `Finish: ${textureType === 'textured' ? 'Textured (Thick)' : 'Smooth (Diluted)'}\n`;
    
    summary += isHebrew
      ? `סילר: ${includeSealer ? 'כן' : 'לא'}\n\n`
      : `Sealer: ${includeSealer ? 'Yes' : 'No'}\n\n`;

    if (calculations) {
      summary += isHebrew ? `כמויות ומחירים משוערים:\n` : `Quantities and Estimated Prices:\n`;
      
      const primerName = isHebrew ? calculations.primer.type?.nameHe : calculations.primer.type?.nameEn;
      summary += isHebrew
        ? `  • ${primerName}: ${calculations.primer.totalGrams} גרם (${calculations.primer.optimal.packsNeeded}x ${calculations.primer.optimal.label}) - ₪${calculations.primer.estimatedPrice}\n`
        : `  • ${primerName}: ${calculations.primer.totalGrams}g (${calculations.primer.optimal.packsNeeded}x ${calculations.primer.optimal.label}) - ₪${calculations.primer.estimatedPrice}\n`;
      
      const microlitName = isHebrew ? calculations.microlit.productName : calculations.microlit.productNameEn;
      summary += isHebrew
        ? `  • ${microlitName}: ${calculations.microlit.totalGrams} גרם (${calculations.microlit.optimal.packsNeeded}x ${calculations.microlit.optimal.label}) - ₪${calculations.microlit.estimatedPrice}\n`
        : `  • ${microlitName}: ${calculations.microlit.totalGrams}g (${calculations.microlit.optimal.packsNeeded}x ${calculations.microlit.optimal.label}) - ₪${calculations.microlit.estimatedPrice}\n`;
      
      if (calculations.sealer) {
        summary += isHebrew
          ? `  • סילר אקרוסיל: ${calculations.sealer.totalMl} מ"ל (${calculations.sealer.optimal.packsNeeded}x ${calculations.sealer.optimal.label}) - ₪${calculations.sealer.estimatedPrice}\n`
          : `  • Acrosil Sealer: ${calculations.sealer.totalMl}ml (${calculations.sealer.optimal.packsNeeded}x ${calculations.sealer.optimal.label}) - ₪${calculations.sealer.estimatedPrice}\n`;
      }
      
      summary += isHebrew
        ? `\n💰 סה"כ משוער: ₪${calculations.totalPrice}\n`
        : `\n💰 Estimated Total: ₪${calculations.totalPrice}\n`;
    }

    summary += isHebrew
      ? `\n---\nM'art - Shahal Building Materials\nWhatsApp: 050-561-5605`
      : `\n---\nM'art - Shahal Building Materials\nWhatsApp: 050-561-5605`;

    return summary;
  }, [substrateDetails, selectedSize, area, selectedColor, textureType, includeSealer, calculations]);

  return {
    // State
    selectedSubstrate,
    selectedSize,
    customArea,
    selectedColor,
    textureType,
    includeSealer,
    area,
    
    // Computed
    substrateDetails,
    selectedPrimer,
    layers,
    hasWarnings,
    calculations,
    
    // Setters
    setSelectedSubstrate,
    setSelectedSize,
    setCustomArea,
    setSelectedColor,
    setTextureType,
    setIncludeSealer,
    clearSelections,
    generateSummary,
  };
};
